import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ChevronRight, AlertTriangle, Clock, Mic } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Sessão UI-1 — NexoSuggestionCard
//
// Deriva conteúdo do briefing com fallback de 4 níveis:
//   1. priority_actions[0]  → cabeçalho com label + CTA voz "Revisar com o Nexo"
//   2. finance_alerts[0]    → alerta financeiro + CTA navegação pra finance
//   3. upcoming_deadlines[0] → próximo deadline + CTA navegação pra calendar
//   4. EmptyState neutro    → "Tudo tranquilo" + CTA "Falar com Nexo"
//
// AnimatePresence com key derivada do conteúdo: re-fetch a cada 15s do
// briefing dispara fade-out + slide-down + fade-in suave (sem flicker).

function derive(briefing) {
  if (!briefing) return { kind: "empty" };

  const actions = briefing.priority_actions || [];
  if (actions.length > 0) {
    const a = actions[0];
    return {
      kind: "action",
      key: `action:${a?.command || a?.label || "0"}`,
      title: a?.label || "Ação prioritária",
      description: actions.length > 1
        ? `Você tem ${actions.length} ações prioritárias agora.`
        : "Esta é a ação mais importante agora, segundo o seu contexto.",
      ctaLabel: "Revisar com o Nexo",
      ctaIcon: Mic,
      onCta: (handlers) => handlers.onAction?.(a?.command || a?.label),
      tone: "cyan",
    };
  }

  const alerts = briefing.finance_alerts || [];
  if (alerts.length > 0) {
    const a = alerts[0];
    const cat = (a?.category || "categoria").toString();
    const pct = a?.percent_above != null ? `${a.percent_above}%` : "acima";
    return {
      kind: "finance",
      key: `finance:${cat}:${pct}`,
      title: `Alerta: ${cat.charAt(0).toUpperCase() + cat.slice(1)} está ${pct} acima da média`,
      description: a?.current != null && a?.average != null
        ? `Atual R$ ${Number(a.current).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} vs média R$ ${Number(a.average).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}.`
        : "Categoria fora do padrão histórico do mês.",
      ctaLabel: "Ver detalhes",
      ctaIcon: ChevronRight,
      onCta: (handlers) => handlers.onNavigate?.("finance"),
      tone: "amber",
    };
  }

  const deadlines = briefing.upcoming_deadlines || [];
  if (deadlines.length > 0) {
    const d = deadlines[0];
    let when = "em breve";
    if (d?.datetime) {
      try { when = formatDistanceToNow(parseISO(d.datetime), { addSuffix: true, locale: ptBR }); }
      catch (_) { /* fallback */ }
    }
    return {
      kind: "deadline",
      key: `deadline:${d?.id || d?.title || "0"}`,
      title: `Você tem "${d?.title || "compromisso"}" ${when}`,
      description: "Confirma na agenda pra não perder.",
      ctaLabel: "Ver agenda",
      ctaIcon: ChevronRight,
      onCta: (handlers) => handlers.onNavigate?.("calendar"),
      tone: "purple",
    };
  }

  return { kind: "empty" };
}

const TONE_BORDER = {
  cyan:   "border-brand-cyan/20",
  amber:  "border-status-warning/20",
  purple: "border-brand-purple/20",
  neutral: "border-subtle",
};

const TONE_ICON_BG = {
  cyan:   "bg-brand-cyan/10 text-brand-cyan",
  amber:  "bg-status-warning/10 text-status-warning",
  purple: "bg-brand-purple/10 text-brand-purple",
  neutral: "bg-surface-card-hover text-foreground-muted",
};

// Ícone secundário acima do título quando faz sentido (alerta financeiro / deadline).
const KIND_INNER_ICON = {
  finance:  AlertTriangle,
  deadline: Clock,
};

export default function NexoSuggestionCard({ briefing, onAction, onNavigate, onTalk }) {
  const content = useMemo(() => derive(briefing), [briefing]);
  const handlers = { onAction, onNavigate };

  const tone = content.tone || "neutral";
  const InnerIcon = KIND_INNER_ICON[content.kind];
  const CtaIcon = content.ctaIcon || ChevronRight;

  return (
    <Card
      variant="elevated"
      className={cn(
        "relative overflow-hidden p-6 md:p-8 min-h-[280px] flex flex-col justify-between",
        // UI-1.5 — glass-card-strong + gradient border + hover glow.
        // Sobrescreve o TONE_BORDER cyan default; pra alerts amber e
        // deadlines purple, mantém border tonal sutil via TONE_BORDER.
        "glass-card-strong gradient-border-cyan glow-hover",
        TONE_BORDER[tone],
      )}
      data-testid="nexo-suggestion-card"
    >
      {/* Gradient radial sutil no canto superior esquerdo */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(circle at 0% 0%, rgba(0,212,255,0.06), transparent 45%)",
        }}
      />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", TONE_ICON_BG.cyan)}>
            <Sparkles className="w-4 h-4" aria-hidden="true" />
          </div>
          <span
            className="text-caption font-semibold uppercase text-brand-cyan/80"
            style={{ letterSpacing: "0.12em" }}
          >
            Sugestão do Nexo
          </span>
        </div>

        {/* Conteúdo dinâmico com microinteração de troca */}
        <AnimatePresence mode="wait">
          {content.kind === "empty" ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
              className="space-y-3"
            >
              <h3 className="font-display font-semibold text-foreground text-h2">
                Tudo tranquilo por aqui
              </h3>
              <p className="text-body text-foreground-muted max-w-md">
                Sem ações urgentes no momento. Você pode planejar a semana ou conversar com o Nexo.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key={content.key}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
              className="space-y-3"
            >
              {InnerIcon && (
                <div className={cn("inline-flex w-8 h-8 rounded-md items-center justify-center", TONE_ICON_BG[tone])}>
                  <InnerIcon className="w-4 h-4" aria-hidden="true" />
                </div>
              )}
              <h3 className="font-display font-semibold text-foreground text-h2 max-w-2xl">
                {content.title}
              </h3>
              {content.description && (
                <p className="text-body text-foreground-muted max-w-2xl">
                  {content.description}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* CTA */}
      <div className="relative mt-6">
        {content.kind === "empty" ? (
          <Button
            variant="primary"
            size="md"
            onClick={() => (onTalk ? onTalk() : onAction?.("organizar meu dia"))}
            className="bg-gradient-to-r from-brand-cyan to-brand-green text-surface-canvas font-semibold"
            data-testid="suggestion-cta-empty"
          >
            <Sparkles className="w-4 h-4" />
            Falar com Nexo
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="md"
            onClick={() => content.onCta?.(handlers)}
            data-testid={`suggestion-cta-${content.kind}`}
          >
            <CtaIcon className="w-4 h-4" />
            {content.ctaLabel}
          </Button>
        )}
      </div>
    </Card>
  );
}
